import { getSectionTypeOptions, getSectionAdapter, getRegisteredAdapters } from "./sectionRegistry";
import { FALLBACK_IMAGE } from "../../../utils/uiConstants";

// Force section-type registration before computing derived values
import "../components/PageSectionRenderer";

/* ─── Derived from registry ─── */

export const sectionTypeOptions = getSectionTypeOptions();

const splitRowPositionOptions = [
  { value: "left", label: "Imagen izquierda / Texto derecha" },
  { value: "right", label: "Imagen derecha / Texto izquierda" },
];

export const structuredSectionConfigs = getRegisteredAdapters()
  .filter((a) => a.editorFields && a.itemLabel)
  .reduce((acc, adapter) => {
    const fields = [...adapter.editorFields];
    if (adapter.type === "filasDivididas") {
      fields.push({
        key: "position",
        label: "Orden visual de la fila",
        input: "select",
        options: splitRowPositionOptions,
      });
    }
    if (adapter.type === "acordeon") {
      const descFieldIdx = fields.findIndex((f) => f.key === "description");
      if (descFieldIdx >= 0) {
        fields[descFieldIdx] = {
          ...fields[descFieldIdx],
          label: "Respuesta o contenido del item",
          placeholder: "Escribe la respuesta aqui...",
        };
      }
    }
    acc[adapter.type] = { fields, itemLabel: adapter.itemLabel };
    return acc;
  }, {});

const legacyTypeMap = getRegisteredAdapters()
  .filter((a) => a.legacyType)
  .reduce((acc, adapter) => {
    acc[adapter.legacyType] = adapter.type;
    return acc;
  }, {});

const normalizeSectionType = (type = "") => legacyTypeMap[type] || type || "encabezado";

const uid = () => Math.random().toString(36).substring(2, 9);

export const createSimpleSection = (rawType = "encabezado", overrides = {}) => {
  const type = normalizeSectionType(rawType || overrides.type || "encabezado");
  const adapter = getSectionAdapter(type);
  let section;
  if (adapter?.getDefaults) {
    section = adapter.getDefaults();
  } else if (type === "texto") {
    section = { type, title: "", paragraphsText: "Escribe aqui tu contenido." };
  } else if (type === "division") {
    section = { type, title: "", description: "" };
  } else {
    section = { type: "encabezado", title: "", subtitle: "", description: "" };
  }
  return { _id: uid(), ...section, ...overrides, type };
};

/* ─── Editor format conversions ─── */

export const sectionsToEditor = (sections = []) =>
  (Array.isArray(sections) ? sections : []).map((section) => {
    const _id = section?._id || uid();
    const type = normalizeSectionType(section?.type);
    if (type === "texto") {
      return { _id, type: "texto", title: section?.title || "", paragraphsText: (section?.paragraphs || []).join("\n\n") };
    }
    if (type === "portada") {
      return { _id, type: "portada", title: section?.title || "", subtitle: section?.subtitle || "", description: section?.description || "", backgroundImage: section?.image?.src || "" };
    }
    if (type === "filasDivididas") {
      return { _id, type: "filasDivididas", title: section?.title || "", itemsText: (section?.items || []).map((item) => {
        const paragraphs = Array.isArray(item?.paragraphs) ? item.paragraphs.join(" /// ") : item?.description || "";
        return `${item?.title || ""} | ${paragraphs} | ${item?.image?.src || ""} | ${item?.reverse ? "right" : "left"}`.trim();
      }).join("\n") };
    }
    if (type === "tarjetasImagen") {
      return { _id, type: "tarjetasImagen", title: section?.title || "", backgroundImage: section?.image?.src || "", itemsText: (section?.items || []).map((item) => `${item?.image?.src || ""} | ${item?.href || ""}`.trim()).join("\n") };
    }
    if (type === "carrusel") {
      return { _id, type: "carrusel", title: section?.title || "", description: section?.description || "", itemsText: (section?.items || []).map((item) => `${item?.image?.src || ""} | ${item?.title || ""} | ${item?.description || ""} | ${item?.href || ""}`.trim()).join("\n") };
    }
    if (type === "lineaTiempo") {
      return { _id, type: "lineaTiempo", title: section?.title || "", itemsText: (section?.items || []).map((item) => `${item?.title || ""} | ${item?.subtitle || ""} | ${item?.description || ""}`.trim()).join("\n") };
    }
    if (type === "acordeon") {
      return {
        _id,
        type: "acordeon",
        title: section?.title || "",
        description: section?.description || "",
        itemsText: (section?.items || [])
          .map((item) => {
            const contentFromParagraphs = Array.isArray(item?.paragraphs) ? item.paragraphs.join(" / ") : "";
            const contentFromNotes = Array.isArray(item?.notes) ? item.notes.join(" / ") : item?.note || "";
            const contentFromSchedule = Array.isArray(item?.schedule) ? item.schedule.join(" / ") : item?.schedule || "";
            const legacyContent = [item?.address || "", item?.description || "", contentFromSchedule, contentFromNotes]
              .map((part) => String(part || "").trim())
              .filter(Boolean)
              .join(" / ");
            const content = contentFromParagraphs || legacyContent;
            return `${item?.title || ""} | ${content}`.trim();
          })
          .join("\n"),
      };
    }
    if (type === "tarjetasAccion") {
      return { _id, type: "tarjetasAccion", title: section?.title || "", description: section?.description || "", itemsText: (section?.items || []).map((item) => `${item?.title || ""} | ${item?.subtitle || ""} | ${item?.button?.label || ""} | ${item?.button?.to || item?.button?.href || ""} | ${item?.image?.src || ""}`.trim()).join("\n") };
    }
    if (type === "franja") {
      return { _id, type: "franja", title: section?.title || "", backgroundImage: section?.image?.src || "", href: section?.to || "" };
    }
    if (type === "bloqueComercial") {
      return {
        _id,
        type: "bloqueComercial",
        title: section?.title || "",
        imageLeft: section?.imageLeft?.src || section?.imageLeft || "",
        toLeft: section?.toLeft || "",
        imageRight: section?.imageRight?.src || section?.imageRight || "",
        toRight: section?.toRight || "",
      };
    }
    if (type === "division") {
      return { _id, type: "division", title: section?.title || "", description: section?.description || "", image: section?.image?.src || "", reverse: !!section?.reverse };
    }
    return { _id, type: "encabezado", title: section?.title || "", subtitle: section?.subtitle || "", description: section?.description || "" };
  });

export const editorToSections = (sections = []) =>
  sections
    .map((section) => {
      const type = normalizeSectionType(section.type);
      if (type === "texto") {
        const paragraphs = String(section.paragraphsText || "").split(/\n{2,}/).map((text) => text.trim()).filter(Boolean);
        return { type: "texto", title: section.title || "", paragraphs };
      }
      if (type === "portada") {
        return { type: "portada", title: section.title || "", subtitle: section.subtitle || "", description: section.description || "", image: { src: section.backgroundImage || FALLBACK_IMAGE, alt: section.title || "Imagen" } };
      }
      if (type === "filasDivididas") {
        const items = String(section.itemsText || "").split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
          const [title, content, imageSrc, reverseFlag] = line.split("|").map((part) => (part || "").trim());
          const hasParagraphs = content.includes(" /// ");
          const paragraphs = hasParagraphs ? content.split(" /// ").map((p) => p.trim()).filter(Boolean) : [];
          return {
            title,
            ...(hasParagraphs ? { paragraphs } : { description: content }),
            image: imageSrc ? { src: imageSrc, alt: title || "Imagen" } : undefined,
            reverse: reverseFlag?.toLowerCase() === "right",
          };
        }).filter((item) => item.title);
        return { type: "filasDivididas", title: section.title || "", items };
      }
      if (type === "tarjetasImagen") {
        const items = String(section.itemsText || "").split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
          const [imageSrc, linkUrl] = line.split("|").map((part) => (part || "").trim());
          return {
            image: { src: imageSrc, alt: section.title || "Imagen" },
            ...(linkUrl ? { href: linkUrl } : {}),
          };
        }).filter((item) => item.image?.src);
        return {
          type: "tarjetasImagen",
          title: section.title || "",
          image: { src: section.backgroundImage || (items.length > 0 ? items[0].image.src : FALLBACK_IMAGE) || "Imagen" },
          items,
        };
      }
      if (type === "carrusel") {
        const items = String(section.itemsText || "").split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
          const [imageSrc, title, description, href] = line.split("|").map((part) => (part || "").trim());
          return {
            image: imageSrc ? { src: imageSrc, alt: title || "Slide" } : undefined,
            title,
            description,
            href: href || undefined,
          };
        }).filter((item) => item.image?.src);
        return { type: "carrusel", title: section.title || "", description: section.description || "", items };
      }
      if (type === "lineaTiempo") {
        const items = String(section.itemsText || "").split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
          const [title, subtitle, description] = line.split("|").map((part) => (part || "").trim());
          return { title, subtitle, description };
        }).filter((item) => item.title);
        return { type: "lineaTiempo", title: section.title || "", items };
      }
      if (type === "acordeon") {
        const items = String(section.itemsText || "").split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
          const [title, ...contentParts] = line.split("|").map((part) => (part || "").trim());
          const contentRaw = contentParts.join(" | ").trim();
          const paragraphs = contentRaw ? contentRaw.split("/").map((entry) => entry.trim()).filter(Boolean) : [];
          return { title, paragraphs: paragraphs.length ? paragraphs : contentRaw ? [contentRaw] : [] };
        }).filter((item) => item.title);
        return { type: "acordeon", title: section.title || "", description: section.description || "", items };
      }
      if (type === "tarjetasAccion") {
        const items = String(section.itemsText || "").split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
          const [title, subtitle, buttonLabel, link, imageSrc] = line.split("|").map((part) => (part || "").trim());
          const hasButton = Boolean(buttonLabel || link);
          const button = hasButton
            ? link.startsWith("http")
              ? { label: buttonLabel || "Ver", href: link }
              : { label: buttonLabel || "Ver", to: link }
            : undefined;
          return { title, subtitle, image: imageSrc ? { src: imageSrc, alt: title || "Imagen" } : undefined, button };
        }).filter((item) => item.title);
        return { type: "tarjetasAccion", title: section.title || "", description: section.description || "", items };
      }
      if (type === "franja") {
        const rawHref = String(section.href || "").trim();
        const rawImg = String(section.backgroundImage || "").trim();
        return { type: "franja", title: section.title || "", image: rawImg ? { src: rawImg, alt: section.title || "Franja" } : null, to: rawHref || "" };
      }
      if (type === "bloqueComercial") {
        const rawImgL = String(section.imageLeft || "").trim();
        const rawImgR = String(section.imageRight || "").trim();
        const rawToL = String(section.toLeft || "").trim();
        const rawToR = String(section.toRight || "").trim();
        return {
          type: "bloqueComercial",
          title: section.title || "",
          imageLeft: rawImgL ? { src: rawImgL, alt: "Imagen izquierda" } : null,
          imageRight: rawImgR ? { src: rawImgR, alt: "Imagen derecha" } : null,
          toLeft: rawToL || "",
          toRight: rawToR || "",
        };
      }
      if (type === "division") {
        const rawImg = String(section.image || "").trim();
        return { type: "division", title: section.title || "", description: section.description || "", image: rawImg ? { src: rawImg, alt: section.title || "Imagen" } : null, reverse: !!section.reverse };
      }
      return { type: "encabezado", title: section.title || "", subtitle: section.subtitle || "", description: section.description || "" };
    })
    .filter((section) => section.type);

export const getStructuredLines = (itemsText = "") =>
  String(itemsText || "").split("\n").map((line) => line.trim()).filter(Boolean);

export const getStructuredEntries = (itemsText = "", fields = []) =>
  getStructuredLines(itemsText).map((line, lineIndex) => {
    const values = line.split("|").map((part) => part.trim());
    const entry = {};
    fields.forEach((field, fieldIndex) => {
      entry[field.key] = values[fieldIndex] || "";
    });
    entry._lineIndex = lineIndex;
    return entry;
  });

export const applySimpleTextMode = (currentValue, selectionStart, selectionEnd, mode) => {
  const value = String(currentValue || "");
  const start = Number.isInteger(selectionStart) ? selectionStart : value.length;
  const end = Number.isInteger(selectionEnd) ? selectionEnd : value.length;
  const selectedText = value.slice(start, end);

  if (mode === "list") {
    const blockStart = value.lastIndexOf("\n", start - 1) + 1;
    const blockEndRaw = value.indexOf("\n", end);
    const blockEnd = blockEndRaw === -1 ? value.length : blockEndRaw;
    const blockText = value.slice(blockStart, blockEnd);
    const inserted = blockText
      .split("\n")
      .map((line) => (line.trim().startsWith("- ") ? line : `- ${line}`))
      .join("\n");
    return {
      value: `${value.slice(0, blockStart)}${inserted}${value.slice(blockEnd)}`,
      selectionStart: blockStart,
      selectionEnd: blockStart + inserted.length,
    };
  }

  const insertedByMode = {
    bold: selectedText ? `**${selectedText}**` : "**texto**",
    italic: selectedText ? `*${selectedText}*` : "*texto*",
    paragraph: "\n\n",
  };
  const inserted = insertedByMode[mode] || selectedText;
  let nextStart = start;
  let nextEnd = start + inserted.length;

  if (mode === "bold") {
    nextStart = start + 2;
    nextEnd = selectedText ? end + 2 : start + 7;
  }
  if (mode === "italic") {
    nextStart = start + 1;
    nextEnd = selectedText ? end + 1 : start + 6;
  }
  if (mode === "paragraph") {
    nextStart = start + inserted.length;
    nextEnd = nextStart;
  }
  return {
    value: `${value.slice(0, start)}${inserted}${value.slice(end)}`,
    selectionStart: nextStart,
    selectionEnd: nextEnd,
  };
};
